import React from "react";
import { Link } from "react-router-dom";
import {
  StickerShoe, StickerStar, StickerBolt, StickerSun, StickerCassette,
  StickerSurf, StickerPeace, StickerFlower, StickerSkate, StickerVan,
  StickerSunflower, StickerCampfire, StickerWave, StickerTree, StickerMeltSmile,
} from "../components/Stickers";

const Milestone = ({ year, title, body, color, sticker }) => (
  <div className="relative bg-white border-4 border-black shadow-[8px_8px_0_#000] p-6 sm:p-8">
    {sticker && <div className="absolute -top-8 -right-8">{sticker}</div>}
    <div className="font-pixel text-xl uppercase tracking-widest text-black/60">{year}</div>
    <h3 className="font-display tracking-wider text-3xl sm:text-4xl mt-1" style={{ color }}>{title}</h3>
    <p className="font-body text-base sm:text-lg mt-3 text-black/85 leading-relaxed">{body}</p>
  </div>
);

const Accomplishments = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[#F87171] border-b-4 border-black relative overflow-hidden">
        <div className="absolute top-8 left-6 hidden md:block"><StickerStar size={90} rotate={-10} className="float-slow" /></div>
        <div className="absolute bottom-8 right-6 hidden md:block"><StickerBolt size={90} rotate={10} className="float-med" /></div>

        <div className="max-w-7xl mx-auto py-20 px-6 text-white">
          <div className="font-pixel uppercase tracking-widest text-lg text-white/80">▒▒ The Big Ones ▒▒</div>
          <h1 className="font-display tracking-wider text-6xl sm:text-7xl lg:text-8xl mt-3">ACCOMPLISHMENTS</h1>
          <p className="font-body text-xl mt-5 max-w-3xl">
            Every kilometre, every dollar raised, every speech. The story below is mine, but none of these numbers
            happened solo. They exist because a community showed up to back them.
          </p>
        </div>
      </section>

      {/* Big stats row */}
      <section className="bg-[#F4D58D] border-b-4 border-black py-10 px-6 relative">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { n: "7,000", l: "km · St. John's → Port Coquitlam" },
            { n: "16th", l: "human to cross Canada on foot" },
            { n: "$100K+", l: "raised for cancer research" },
            { n: "280", l: "km · PEI · 65 hrs · no sleep" },
            { n: "1,000", l: "books sold on the cross-Canada tour" },
            { n: "5,000+", l: "students inspired in person" },
          ].map((s) => (
            <div key={s.l} className="bg-black text-white border-4 border-black p-6 shadow-[8px_8px_0_#F87171]" data-testid={`accomplishment-stat-${s.n}`}>
              <div className="font-display text-5xl tracking-wider text-[#F4D58D]">{s.n}</div>
              <div className="font-pixel text-xl mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline / Milestones */}
      <section className="py-20 px-6 relative overflow-hidden">
        <div className="absolute top-20 right-8 hidden md:block"><StickerSun size={90} rotate={12} className="float-slow" /></div>
        <div className="absolute bottom-20 left-8 hidden md:block"><StickerSunflower size={100} rotate={-14} className="float-med" /></div>
        <div className="absolute top-[40%] left-[2%] hidden lg:block"><StickerMeltSmile size={90} rotate={10} className="float-slow" /></div>
        <div className="absolute bottom-20 right-[2%] hidden lg:block"><StickerWave size={100} rotate={-8} className="float-med" /></div>

        <div className="max-w-6xl mx-auto">
          <h2 className="font-display tracking-wider text-5xl sm:text-6xl text-black">THE BIG MILESTONES</h2>
          <div className="hazard h-3 mt-4 mb-12" />

          <div className="grid md:grid-cols-2 gap-8">
            <Milestone
              year="2023 · 160 days · east to west"
              title="7,000 km Across Canada"
              body="Laced up in St. John's, Newfoundland and ran to Terry Fox's resting place in Port Coquitlam, BC. Slept in a 1985 VW Vanagon every night, livestreamed the whole way, and was picked up by local and national news from coast to coast. Raised $100,000 for cancer research. 16th human ever to cross Canada on foot."
              color="#F87171"
              sticker={<StickerShoe size={90} rotate={14} />}
            />
            <Milestone
              year="self-published · 50 cities · 65 days"
              title="The Book + Cross-Canada Tour"
              body="Wrote the story, self-published it, picked up the printed copies in person, and drove a minivan across the country a second time selling them. 1,000 books sold and another $6,070 raised for cancer patients. Every signed copy moved because the community kept showing up."
              color="#F4A261"
              sticker={<StickerCassette size={110} rotate={-10} />}
            />
            <Milestone
              year="65 hours · no sleep"
              title="280 km Across PEI"
              body="Ran the entire length of Prince Edward Island in 65 hours without sleep. Second human ever to do it."
              color="#B57FA0"
              sticker={<StickerBolt size={80} rotate={18} />}
            />
            <Milestone
              year="Terry Fox Foundation"
              title="Ambassador & National Speaker"
              body="2024 New Brunswick ambassador for the Terry Fox Foundation and a current national guest speaker for the Foundation. Over 5,000 students have heard the story in person, and the goal is to keep that number climbing."
              color="#5BA8C7"
              sticker={<StickerStar size={80} rotate={-8} />}
            />
          </div>
        </div>
      </section>

      {/* Future goals, under construction */}
      <section className="bg-[#0A0A0A] text-white py-20 px-6 border-y-4 border-black relative overflow-hidden">
        <div className="absolute top-10 left-10 hidden md:block"><StickerVan size={130} rotate={-10} className="float-slow" /></div>
        <div className="absolute bottom-10 right-10 hidden md:block"><StickerSurf size={120} rotate={14} className="float-med" /></div>

        <div className="max-w-6xl mx-auto">
          <div className="inline-block bg-[#F87171] text-white border-4 border-black px-4 py-1 font-display tracking-wider text-2xl shadow-[4px_4px_0_#000] mb-4">
            ⚡ UP NEXT ⚡
          </div>
          <h2 className="font-display tracking-wider text-5xl sm:text-6xl text-[#F4D58D]">WHAT'S NEXT</h2>
          <p className="font-body text-xl text-white/80 max-w-3xl mt-4">
            My son is my priority. Full-time dad first. These are the dreams I'm circling back to when the time is right.
          </p>

          <ul className="grid md:grid-cols-3 gap-6 mt-10">
            {[
              { t: "Manitoba Again", b: "A second run across my home province, either the record or something more interesting, filmed and live-streamed end to end.", c: "#F4D58D" },
              { t: "Run Across the USA", b: "Coast to coast in the lower 48. Same idea, same cause, same livestream.", c: "#F87171" },
              { t: "Start a Nonprofit", b: "Built to directly support cancer patients and the research that gets us closer to a cure.", c: "#8ECAE6" },
            ].map((g) => (
              <li key={g.t} className="bg-white text-black border-4 border-black shadow-[8px_8px_0_#F4D58D] p-6" data-testid={`future-goal-${g.t}`}>
                <input type="checkbox" disabled className="w-6 h-6 border-4 border-black mr-2 align-middle" />
                <span className="font-display tracking-wider text-2xl align-middle" style={{ color: g.c }}>{g.t}</span>
                <p className="font-body text-base mt-3">{g.b}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center relative">
        <div className="absolute top-6 left-1/4 hidden md:block"><StickerPeace size={70} rotate={-15} className="float-slow" /></div>
        <div className="absolute top-6 right-1/4 hidden md:block"><StickerFlower size={70} rotate={20} className="float-med" /></div>
        <h3 className="font-display tracking-wider text-4xl sm:text-5xl text-black">Want the long version?</h3>
        <p className="font-body text-lg mt-3 max-w-2xl mx-auto text-black/80">
          I tell this story to classrooms, fundraisers, and full auditoriums. If you've got an audience, I've got a story for them.
        </p>
        <Link to="/speaking" data-testid="accomplishments-cta-book"
          className="inline-block mt-6 font-display text-2xl tracking-wider px-7 py-3 bg-[#F87171] text-white border-4 border-black shadow-[8px_8px_0_#000] btn-press">
          Book me to speak →
        </Link>
      </section>
    </div>
  );
};

export default Accomplishments;
